import React, { useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const GAME_LABELS = {
  math: {
    key: 'math',
    label: l => l.math,
    modes: [
      { key: 'multiplication', label: l => l.multiplication },
      { key: 'division', label: l => l.division },
      { key: 'plusminus', label: l => l.plusminus },
      { key: 'tripleplusminus', label: l => l.tripleplusminus },
    ]
  },
  reading: {
    key: 'reading',
    label: l => l.title,
    modes: [
      { key: 'flash', label: l => l.flash },
      { key: 'multiple', label: l => l.multiple },
      { key: 'timed', label: l => l.timed }
    ]
  },
  listening: {
    key: 'listening',
    label: l => l.listening,
    modes: []
  }
};

const ProgressHistory = ({ onBack, labels }) => {
  const profile = JSON.parse(localStorage.getItem("blitzlesenUser") || "{}");
  const [selectedGame, setSelectedGame] = useState("");
  const [selectedMode, setSelectedMode] = useState("");

  if (!profile.username) {
    return (
      <div className="App">
        <h2>No profile found.</h2>
        <button onClick={onBack}>Back</button>
      </div>
    );
  }

  const { scores = {} } = profile;

  // Determine available games and modes from scores
  const availableGames = Object.keys(scores).reduce((acc, mode) => {
    if (["multiplication", "division", "plusminus"].includes(mode)) {
      if (!acc.includes("math")) acc.push("math");
    } else if (["flash", "multiple", "timed"].includes(mode)) {
      if (!acc.includes("reading")) acc.push("reading");
    } else if (["listening"].includes(mode)) {
      if (!acc.includes("listening")) acc.push("listening");
    }
    return acc;
  }, []);

  // Modes for selected game
  let availableModes = [];
  if (selectedGame && GAME_LABELS[selectedGame]) {
    availableModes = GAME_LABELS[selectedGame].modes.filter(m => scores[m.key]);
  }

  // Filtered scores
  let filteredScores = [];
  if (selectedMode && scores[selectedMode]) {
    filteredScores = scores[selectedMode];
  } else if (selectedGame && GAME_LABELS[selectedGame]) {
    // If no mode is selected, show all scores for all modes of the selected game
    const modeKeys = GAME_LABELS[selectedGame].modes.map(m => m.key);
    if (modeKeys.length > 0) {
      filteredScores = modeKeys.flatMap(key => scores[key] || []);
    } else if (scores[selectedGame]) {
      // For games with no modes (like listening)
      filteredScores = scores[selectedGame];
    }
  }

  return (
    <div className="App" style={{ maxWidth: 480, margin: '0 auto' }}>
      {profile.avatar && (
        <div style={{ fontSize: "3rem", marginBottom: "10px", display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {typeof profile.avatar === "string" && profile.avatar.startsWith("data:image/") ? (
            <img
              src={profile.avatar}
              alt="Avatar"
              style={{ width: "48px", height: "48px", borderRadius: "50%", objectFit: "cover", marginRight: 8 }}
            />
          ) : (
            <span style={{ fontSize: 48, marginRight: 8 }}>{profile.avatar}</span>
          )}
        </div>
      )}
      <h2>{labels.progressTitle} {profile.username}</h2>

      {/* Filter by Game */}
      <div style={{ marginBottom: 16 }}>
        <label htmlFor="gameSelect" style={{ fontWeight: 600 }}>{labels.filterByGame || "Filter by game"}:</label>
        <select
          id="gameSelect"
          value={selectedGame}
          onChange={e => {
            setSelectedGame(e.target.value);
            setSelectedMode("");
          }}
          style={{ marginLeft: 8, fontSize: '1.1em', padding: '6px 12px', borderRadius: 8 }}
        >
          <option value="">{labels.allCategories || "All"}</option>
          {availableGames.map(game => (
            <option key={game} value={game}>{GAME_LABELS[game]?.label(labels)}</option>
          ))}
        </select>
      </div>

      {/* Filter by Mode */}
      {selectedGame && availableModes.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <label htmlFor="modeSelect" style={{ fontWeight: 600 }}>{labels.filterByMode || "Filter by mode"}:</label>
          <select
            id="modeSelect"
            value={selectedMode}
            onChange={e => setSelectedMode(e.target.value)}
            style={{ marginLeft: 8, fontSize: '1.1em', padding: '6px 12px', borderRadius: 8 }}
          >
            <option value="">{(labels.allForGame ? labels.allForGame.replace('{game}', GAME_LABELS[selectedGame]?.label(labels)) : (labels.allCategories || 'All'))}</option>
            {availableModes.map(mode => (
              <option key={mode.key} value={mode.key}>{mode.label(labels)}</option>
            ))}
          </select>
        </div>
      )}

      {/* Reset scores button */}
      <button
        onClick={() => {
          if (window.confirm(labels.confirmReset || "Are you sure you want to reset all scores?")) {
            const updatedProfile = { ...profile, scores: {} };
            localStorage.setItem("blitzlesenUser", JSON.stringify(updatedProfile));
            window.location.reload();
          }
        }}
        className="green-button"
        style={{ marginBottom: 20 }}
      >
        {labels.resetScores || "Reset Scores"}
      </button>

      {/* Show results for selected mode or for game with no modes */}
      {(selectedMode || (selectedGame && filteredScores.length > 0)) && (
        <div>
          <h3 style={{ marginTop: 16 }}>
            {selectedMode
              ? (availableModes.find(m => m.key === selectedMode)?.label(labels) || selectedMode)
              : (GAME_LABELS[selectedGame]?.label(labels) || selectedGame)}
          </h3>
          {filteredScores.length > 1 && (
            <Line
              data={{
                labels: filteredScores.map((entry, i) => `#${i + 1}`),
                datasets: [
                  {
                    label: `${selectedMode
                      ? (availableModes.find(m => m.key === selectedMode)?.label(labels) || selectedMode)
                      : (GAME_LABELS[selectedGame]?.label(labels) || selectedGame)} ${labels.score || 'Score'}`,
                    data: filteredScores.map(entry => entry.score),
                    borderColor: "rgba(75,192,192,1)",
                    backgroundColor: "rgba(75,192,192,0.2)",
                    tension: 0.3
                  }
                ]
              }}
              options={{
                responsive: true,
                plugins: {
                  legend: {
                    position: "top"
                  },
                  title: {
                    display: true,
                    text: `${labels.scoreTrendTitle} ${selectedMode
                      ? (availableModes.find(m => m.key === selectedMode)?.label(labels) || selectedMode)
                      : (GAME_LABELS[selectedGame]?.label(labels) || selectedGame)}`
                  }
                }
              }}
              style={{ maxWidth: "100%", margin: "20px 0" }}
            />
          )}
          {filteredScores.length === 0 ? (
            <p>{labels.noResults}</p>
          ) : (
            <ul className="score-history">
              {filteredScores.map((score, index) => (
                <li key={index}>
                  {labels.result || 'Game'} {index + 1}: {score.score}
                  {/* Show total for listening */}
                  {selectedGame === 'listening' || selectedMode === 'listening' ?
                    ` / 5` : ''}
                  —{" "}
                  {new Date(score.timestamp).toLocaleString("de-DE", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit"
                  })}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <button onClick={onBack} className="green-button" style={{ marginTop: "20px", fontSize: "1.3rem", padding: "18px 36px" }}>
        {labels.backToGame}
      </button>
    </div>
  );
};

export default ProgressHistory;